import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { KnowledgeDocument } from '@/types/knowledge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const editSchema = z.object({
  category: z.string().optional(),
  title: z.string().optional(),
  problem: z.string().optional(),
  solution: z.string().optional(),
  editReason: z.string().min(1, 'Motivo da edição é obrigatório'),
  status: z.enum(['draft', 'published', 'deprecated']).optional(),
});

type EditFormData = z.infer<typeof editSchema>;

interface DocumentEditFormProps {
  document: KnowledgeDocument;
  onSubmit: (data: EditFormData) => void;
  isLoading?: boolean;
}

export function DocumentEditForm({ document, onSubmit, isLoading }: DocumentEditFormProps) {
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<EditFormData>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      category: document.category,
      title: document.title,
      problem: document.problem,
      solution: document.solution,
      status: document.status,
    },
  });

  const status = watch('status');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <Label htmlFor="category">Categoria</Label>
        <Input id="category" {...register('category')} />
        {errors.category && (
          <p className="text-sm text-red-500 mt-1">{errors.category.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="title">Título</Label>
        <Input id="title" {...register('title')} />
        {errors.title && (
          <p className="text-sm text-red-500 mt-1">{errors.title.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="problem">Problema</Label>
        <Textarea id="problem" rows={5} {...register('problem')} />
        {errors.problem && (
          <p className="text-sm text-red-500 mt-1">{errors.problem.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="solution">Solução</Label>
        <Textarea id="solution" rows={8} {...register('solution')} />
        {errors.solution && (
          <p className="text-sm text-red-500 mt-1">{errors.solution.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="status">Status</Label>
        <Select value={status} onValueChange={(value) => setValue('status', value as 'draft' | 'published' | 'deprecated')}>
          <SelectTrigger id="status">
            <SelectValue placeholder="Selecione um status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="draft">Rascunho</SelectItem>
            <SelectItem value="published">Publicado</SelectItem>
            <SelectItem value="deprecated">Descontinuado</SelectItem>
          </SelectContent>
        </Select>
        {errors.status && (
          <p className="text-sm text-red-500 mt-1">{errors.status.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="editReason">Motivo da Edição *</Label>
        <Textarea
          id="editReason"
          rows={2}
          placeholder="Descreva o motivo desta edição..."
          {...register('editReason')}
        />
        {errors.editReason && (
          <p className="text-sm text-red-500 mt-1">{errors.editReason.message}</p>
        )}
      </div>

      <Button type="submit" disabled={isLoading}>
        {isLoading ? 'Salvando...' : 'Salvar Alterações'}
      </Button>
    </form>
  );
}
